import React from "react";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ReplayIcon from "@mui/icons-material/Replay";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

// Barra que aparece NO LUGAR da barra de digitação quando não se pode responder.
// Antes cada bloqueio se comportava de um jeito: status fechado/aguardando
// deixava o input visível mas todo desabilitado (só um placeholder explicava),
// e ticket de outro atendente simplesmente não renderizava nada. Aqui todos
// falam a mesma língua: ícone + aviso dizendo o que fazer.
//
// `tom` é o nome de uma cor da paleta (success/warning/info) ou "neutro".
const MOTIVOS = {
	ia: {
		icone: SmartToyIcon,
		tom: "success",
		texto: () => "IA ativa neste atendimento — desative a IA no cabeçalho para responder manualmente.",
	},
	// Mesmo bloqueio, para quem não tem o botão de IA no cabeçalho: mandar desativar
	// ali seria mandar clicar em nada. Quem decide qual dos dois é o Ticket, pelo
	// helper podeAlternarIa — a mesma regra que faz o botão aparecer.
	iaSemBotao: {
		icone: SmartToyIcon,
		tom: "success",
		texto: () => "IA ativa neste atendimento — você não tem permissão para desativá-la.",
	},
	fechado: {
		icone: ReplayIcon,
		tom: "warning",
		texto: () => "Atendimento encerrado — clique em REABRIR no cabeçalho para responder.",
	},
	aguardando: {
		icone: HowToRegIcon,
		tom: "info",
		texto: () => "Atendimento ainda não aceito — clique em ACEITAR no cabeçalho para responder.",
	},
	// Quem não é dono nem gestor precisa ASSUMIR o lead pra responder — é a régua do
	// backend (helpers/AcessoTicket), então o aviso manda fazer isso em vez de só
	// constatar o bloqueio.
	outroAtendente: {
		icone: LockOutlinedIcon,
		tom: "neutro",
		texto: (ticket) => ticket?.user?.name
			? `Atendimento de ${ticket.user.name} — assuma o lead pelo menu do cabeçalho para responder.`
			: "Você não é o responsável por este atendimento — assuma o lead pelo menu do cabeçalho para responder.",
	},
	// Mesmo bloqueio, para quem não tem "Assumir lead" no menu (permissão
	// renderBotaoAssumir, do cadastro no ERP): mandar assumir seria mandar clicar em
	// nada. Mesma dupla de ia/iaSemBotao.
	outroAtendenteSemAssumir: {
		icone: LockOutlinedIcon,
		tom: "neutro",
		texto: (ticket) => ticket?.user?.name
			? `Atendimento de ${ticket.user.name} — somente o responsável ou um gestor pode responder.`
			: "Você não é o responsável por este atendimento — somente o responsável ou um gestor pode responder.",
	},
	// Grupo não é atribuído a ninguém: o que dá (ou não dá) permissão é a conexão.
	// Cai aqui quem chegou no grupo pela URL sem ter a conexão vinculada.
	grupoSemConexao: {
		icone: LockOutlinedIcon,
		tom: "neutro",
		texto: (ticket) => ticket?.whatsapp?.name
			? `Você não tem acesso à conexão ${ticket.whatsapp.name} — só quem atende nessa conexão pode responder no grupo.`
			: "Você não tem acesso à conexão deste grupo — só quem atende nessa conexão pode responder.",
	},
};

const useStyles = makeStyles((theme) => ({
	barra: {
		flex: "none",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1),
		padding: theme.spacing(1.5, 2),
		textAlign: "center",
		color: theme.palette.text.secondary,
		// Mesma escala de fonte dos botões e abas (segue a configuração do usuário).
		fontSize: theme.typography.button.fontSize,
		borderTop: `1px solid ${theme.palette.divider}`,
	},

	icone: {
		fontSize: 18,
		flexShrink: 0,
	},
}));

const InputBloqueado = ({ motivo, ticket }) => {
	const classes = useStyles();
	const theme = useTheme();

	const config = MOTIVOS[motivo];
	if (!config) return null;

	const escuro = theme.palette.type === "dark";
	const paleta = config.tom === "neutro" ? null : theme.palette[config.tom];
	const fundo = paleta
		? alpha(paleta.main, escuro ? 0.16 : 0.1)
		: theme.palette.action.hover;
	const corIcone = paleta
		? (escuro ? paleta.light : paleta.dark)
		: theme.palette.text.secondary;

	const Icone = config.icone;

	return (
		<div className={classes.barra} style={{ backgroundColor: fundo }}>
			<Icone className={classes.icone} style={{ color: corIcone }} />
			{config.texto(ticket)}
		</div>
	);
};

export default InputBloqueado;
